import { Fragment, useRef, useState, useEffect, type KeyboardEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Form, FormField, SelectNew, TextField, PhoneField, Checkbox, Text, useMelioForm } from '@melio/penny';

import { useApp, Button, Banner, Icon, IconButton, Illustration, MelioLogo, NakedButton } from '@/components/ds';
import { Tracker, Spinner } from '@/components/code';
import { IndustryField, AddressFields } from '@/components/form';
import { portalSignup, portalRegisterPartner } from '@/api/payouts';
import { LOCAL_ADMIN_TOKEN } from '@/config/environments';
import type { Go, WithChildren } from '@/lib/types';

// onboarding.tsx — self-serve signup → account type → identity → first business (KYC/KYB) → API key → success.
// Flow: account (email+pw) → type (business|platform) → you (identity) →
//   for business: business info → legal info+address → operating address → key → done
//   for platform: key → done (no business sub-steps; business added from dashboard)
// The PennyProvider/IntlProvider wrapping is now applied app-wide in lib/penny (PennyRoot).

const emptyAddress = { _search: undefined, line1: '', line2: '', city: '', state: '', postalCode: '', aptNumber: '' };

type AccountType = 'business' | 'platform';
type Step = 'account' | 'type' | 'you' | 'business' | 'key' | 'done';

interface FieldSpec {
  name: string;
  label: string;
  test?: (v: any) => boolean;
  message?: string;
}

const businessTypeOptions = [
  {
    value: 'sole-proprietorship',
    label: 'Sole Proprietorship',
    description: 'An unincorporated one-person business (e.g., freelancer, independent contractor).',
  },
  { value: 'partnership', label: 'Partnership', description: 'A business owned by two or more people (e.g., General, LP, LLP).' },
  {
    value: 'llc',
    label: 'Limited Liability Company (LLC)',
    description: 'A registered business structure that protects owners from personal liability.',
  },
  { value: 'corporation', label: 'Corporation', description: 'A registered legal entity (e.g., C-Corp, S-Corp, Inc.).' },
  { value: 'trust', label: 'Trust', description: 'A fiduciary arrangement managed by a trustee on behalf of beneficiaries.' },
  {
    value: 'non-profit',
    label: 'Non-Profit',
    description: 'A tax-exempt organization operating for charitable, educational, or public purposes.',
  },
  { value: 'municipality', label: 'Municipality', description: 'A local government entity such as a city, town, or county.' },
  { value: 'ngo', label: 'NGO', description: 'A non-governmental organization operating independently of government.' },
];

const defaultTaxTypeForBusinessType = (type: string) => (type === 'sole-proprietorship' ? 'ssn' : 'ein');

const taxTypeOptions = [
  { value: 'ein', label: 'EIN' },
  { value: 'ssn', label: 'SSN' },
  { value: 'itin', label: 'ITIN' },
];

// Validation spec lists — defined at module level (no closure deps) so refs stay stable.
const accountSpecs: FieldSpec[] = [{ name: 'email', label: 'Work email' }];
const aboutYouSpecs: FieldSpec[] = [
  { name: 'ownerFirst', label: 'First name' },
  { name: 'ownerLast', label: 'Last name' },
  { name: 'dob', label: 'Date of birth', test: (v) => v instanceof Date && !isNaN(v as unknown as number) },
  { name: 'ownerPhone', label: 'Personal phone number' },
];
const businessInfoSpecs: FieldSpec[] = [
  { name: 'legalName', label: 'Business name' },
  { name: 'industry', label: 'Industry', test: (v) => !!(v && v.naicsCode), message: 'Select an industry' },
  { name: 'phone', label: 'Business phone' },
];
const legalInfoSpecs: FieldSpec[] = [{ name: 'taxId', label: 'Tax ID' }];
const legalAddressSpecs: FieldSpec[] = [
  { name: 'legalAddress.line1', label: 'Address line 1' },
  { name: 'legalAddress.city', label: 'City' },
  { name: 'legalAddress.state', label: 'State' },
  { name: 'legalAddress.postalCode', label: 'Postal code' },
];
const addressSpecs: FieldSpec[] = [
  { name: 'address.line1', label: 'Address line 1' },
  { name: 'address.city', label: 'City' },
  { name: 'address.state', label: 'State' },
  { name: 'address.postalCode', label: 'Postal code' },
];

function SectionTitle({ children }: WithChildren) {
  return (
    <div style={{ gridColumn: '1 / -1', margin: '20px 0 4px' }}>
      <Text textStyle="heading3Semi">{children}</Text>
    </div>
  );
}

interface ChoiceCardProps {
  icon: string;
  title: string;
  desc: string;
  selected?: boolean;
  onClick?: () => void;
}

// Account-type choice card used on the 'type' step.
function ChoiceCard({ icon, title, desc, selected, onClick }: ChoiceCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        gap: 16,
        alignItems: 'flex-start',
        textAlign: 'left',
        width: '100%',
        cursor: 'pointer',
        padding: 18,
        borderRadius: 14,
        background: selected ? 'var(--accent-soft)' : '#fff',
        border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border-static)'}`,
        transition: 'border-color .15s, background-color .15s',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 11,
          flexShrink: 0,
          background: selected ? 'var(--accent)' : 'var(--accent-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={icon} size={22} color={selected ? '#fff' : 'var(--accent)'} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15.5, fontWeight: 600 }}>{title}</div>
        <p style={{ fontSize: 13.5, lineHeight: '20px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>{desc}</p>
      </div>
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          flexShrink: 0,
          marginTop: 2,
          border: `2px solid ${selected ? 'var(--accent)' : 'var(--border-interactive)'}`,
          background: selected ? 'var(--accent)' : '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {selected && <Icon name="checked" size={12} color="#fff" />}
      </div>
    </button>
  );
}

interface SubStepperProps {
  steps: string[];
  current: number;
}

// Compact horizontal sub-step indicator shown inside the business step.
function SubStepper({ steps, current }: SubStepperProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
      {steps.map((label, i) => {
        const done = i < current,
          active = i === current;
        return (
          <Fragment key={i}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  background: done || active ? 'var(--accent)' : 'var(--neutral-300)',
                  color: done || active ? '#fff' : 'var(--text-secondary)',
                }}
              >
                {done ? <Icon name="checked" size={13} color="#fff" /> : i + 1}
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: done || active ? 'var(--text-primary)' : 'var(--text-secondary)',
                }}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? 'var(--accent)' : 'var(--border-static)', margin: '0 10px' }} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

interface KeyRevealProps {
  apiKey: string;
  onContinue?: () => void;
}

function KeyReveal({ apiKey, onContinue }: KeyRevealProps) {
  const [masked, setMasked] = useState(false);
  const [stored, setStored] = useState(false);
  const [copied, setCopied] = useState(false);
  const display = masked ? apiKey.slice(0, 8) + '•'.repeat(apiKey.length - 8) : apiKey;
  const copy = () => {
    try {
      navigator.clipboard.writeText(apiKey);
    } catch (e) {}
    setCopied(true);
    setStored(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div style={{ animation: 'om-fade-up .3s ease' }}>
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: 'var(--accent-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 18,
        }}
      >
        <Icon name="lock" size={26} color="var(--accent)" />
      </div>
      <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Your secret API key</h2>
      <p style={{ fontSize: 15, lineHeight: '23px', color: 'var(--text-secondary)', margin: '10px 0 22px' }}>
        This is the only time we'll show it in full. Copy it now and store it somewhere safe — like a secrets manager.
      </p>
      <div
        style={{
          background: '#16171f',
          borderRadius: 12,
          border: '1px solid #2c2f3d',
          padding: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Icon name="lock" size={16} color="#5c6472" />
        <code className="om-mono" style={{ flex: 1, fontSize: 14, color: '#8fd6a8', letterSpacing: '.01em', wordBreak: 'break-all' }}>
          {display}
        </code>
        <IconButton
          name="document"
          ariaLabel="toggle mask"
          iconSize={16}
          color="#9aa3b2"
          hoverBg="#242736"
          onClick={() => setMasked((m) => !m)}
        />
        <button
          onClick={copy}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 34,
            padding: '0 14px',
            borderRadius: 8,
            border: 'none',
            background: copied ? '#1c3a2a' : 'var(--accent)',
            color: copied ? '#8fd6a8' : '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background-color .15s',
          }}
        >
          <Icon name={copied ? 'checked' : 'document'} size={15} color="currentColor" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <Banner kind="warning" style={{ marginTop: 16 }}>
        Treat this like a password. Anyone with this key can move money on your account. Never expose it in a browser or commit it to git.
      </Banner>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={stored}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStored(e.target.checked)}
          style={{ width: 18, height: 18, accentColor: 'var(--accent)' }}
        />
        <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>I've stored my API key in a safe place</span>
      </label>
      <Button fullWidth size="large" style={{ marginTop: 22 }} disabled={!stored} rightIcon="arrow-right" onClick={onContinue}>
        Continue to dashboard
      </Button>
    </div>
  );
}

interface OnboardingProps {
  go: Go;
}

function Onboarding({ go }: OnboardingProps) {
  const app = useApp();
  const location = useLocation();
  const resumeEmail: string | undefined = (location.state as any)?.resumeEmail;

  // Steps: 'account' | 'type' | 'you' | 'business' | 'key' | 'done'
  // Business sub-steps: 0 (info) | 1 (legal + legal address) | 2 (operating address)
  const [step, setStep] = useState<Step>(resumeEmail ? 'type' : 'account');
  const [bizSub, setBizSub] = useState(0);

  const [accountType, setAccountType] = useState<AccountType | null>(null); // 'business' | 'platform'
  const [adminToken, setAdminToken] = useState(app.env === 'localhost' ? LOCAL_ADMIN_TOKEN : '');
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [adminErr, setAdminErr] = useState('');
  const [issuedKey, setIssuedKey] = useState('');

  const isPlatform = accountType === 'platform';

  // Refs for re-validation subscription (avoid stale closures)
  const stepAttemptedRef = useRef(false);
  const currentSpecsRef = useRef<FieldSpec[]>([]);

  const goStep = (s: Step) => {
    setStep(s);
    setBizSub(0);
    setSubmitErr('');
    stepAttemptedRef.current = false;
  };
  const goBizSub = (n: number) => {
    setBizSub(n);
    setSubmitErr('');
    stepAttemptedRef.current = false;
  };

  const form: any = useMelioForm({
    defaultValues: {
      email: '',
      pw: '',
      pw2: '',
      ownerFirst: '',
      ownerLast: '',
      ownerPhone: '',
      dob: null,
      legalName: '',
      industry: undefined,
      phone: '',
      website: '',
      businessType: 'llc',
      taxIdType: 'ein',
      taxId: '',
      description: '',
      address: { ...emptyAddress },
      legalAddress: { ...emptyAddress },
      operatingSameAsLegal: true,
    },
    onSubmit: () => {
      if (step === 'business' && bizSub === 2) createPartner();
      if (step === 'you' && isPlatform) createPartnerPlatform();
    },
  });
  const { formProps, registerField, watch, getValues, setValue, setError, clearErrors } = form;

  const taxIdType = watch('taxIdType');
  const businessType = watch('businessType');
  const operatingSameAsLegal = watch('operatingSameAsLegal');

  useEffect(() => {
    setValue('taxIdType', defaultTaxTypeForBusinessType(businessType));
  }, [businessType]);

  // Keep currentSpecsRef in sync with the active step/substep so the watch
  // subscription below (set up once) can re-validate without stale closures.
  useEffect(() => {
    if (step === 'account') currentSpecsRef.current = accountSpecs;
    else if (step === 'you') currentSpecsRef.current = aboutYouSpecs;
    else if (step === 'business') {
      if (bizSub === 0) currentSpecsRef.current = businessInfoSpecs;
      else if (bizSub === 1) currentSpecsRef.current = legalInfoSpecs;
      else if (bizSub === 2) currentSpecsRef.current = operatingSameAsLegal ? legalAddressSpecs : [...legalAddressSpecs, ...addressSpecs];
    } else {
      currentSpecsRef.current = [];
    }
  }, [step, bizSub, operatingSameAsLegal]);

  // Validate on change after the step has been attempted (Continue clicked).
  // This is the "re-validate on change after field is visited" behavior.
  useEffect(() => {
    const sub = watch(() => {
      if (!stepAttemptedRef.current) return;
      const specs = currentSpecsRef.current;
      if (!specs.length) return;
      // Clear only current step's fields then re-evaluate
      specs.forEach((s) => clearErrors(s.name));
      const v = getValues();
      const get = (path: string) => path.split('.').reduce<any>((o, k) => (o == null ? o : o[k]), v);
      let firstMsg = '';
      for (const s of specs) {
        const val = get(s.name);
        const ok = s.test ? s.test(val) : !(val == null || val === '');
        if (!ok) {
          setError(s.name, { type: 'required', message: s.message || 'Required' });
          if (!firstMsg) firstMsg = `Please complete: ${s.label}`;
        }
      }
      setSubmitErr(firstMsg);
    });
    return () => sub?.unsubscribe?.();
  }, []);

  // Validate a set of specs, set field-level errors, return first message.
  const checkRequired = (specs: FieldSpec[]) => {
    specs.forEach((s) => clearErrors(s.name));
    const v = getValues();
    const get = (path: string) => path.split('.').reduce<any>((o, k) => (o == null ? o : o[k]), v);
    let firstMsg = '';
    for (const s of specs) {
      const val = get(s.name);
      const ok = s.test ? s.test(val) : !(val == null || val === '');
      if (!ok) {
        setError(s.name, { type: 'required', message: s.message || 'Required' });
        if (!firstMsg) firstMsg = `Please complete: ${s.label}`;
      }
    }
    return firstMsg;
  };

  // Advance helper: validate specs and proceed; marks step as attempted for re-validation.
  const advance = (specs: FieldSpec[] | (() => FieldSpec[]), callback: () => void) => {
    stepAttemptedRef.current = true;
    const err = checkRequired(typeof specs === 'function' ? specs() : specs);
    if (err) {
      setSubmitErr(err);
      return;
    }
    setSubmitErr('');
    stepAttemptedRef.current = false;
    callback();
  };

  // Account step: validate email + password, then create the portal user immediately.
  const goToType = async () => {
    stepAttemptedRef.current = true;
    const v = getValues();
    let err = checkRequired(accountSpecs);
    if (!err && !v.pw) {
      setError('pw', { type: 'required', message: 'Create a password.' });
      err = 'Create a password.';
    }
    if (!err && v.pw !== v.pw2) {
      setError('pw2', { type: 'validate', message: 'Passwords do not match.' });
      err = 'Passwords do not match.';
    }
    if (err) {
      setSubmitErr(err);
      return;
    }
    setSubmitErr('');
    stepAttemptedRef.current = false;
    setSubmitting(true);
    try {
      const { accessToken } = await portalSignup({ env: app.env }, v.email, v.pw);
      app.signIn({ email: v.email, portalToken: accessToken });
      goStep('type');
    } catch (ex: any) {
      const is409 = ex?.status === 409;
      setSubmitErr(is409 ? 'An account with this email already exists. Sign in instead.' : ex?.message || 'Account creation failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const goToYou = () => {
    if (!accountType) {
      setSubmitErr('Please select an account type.');
      return;
    }
    setSubmitErr('');
    goStep('you');
  };

  const goToBusinessOrKey = () =>
    advance(aboutYouSpecs, () => {
      if (isPlatform) createPartnerPlatform();
      else goStep('business');
    });

  const goToLegal = () => advance(businessInfoSpecs, () => goBizSub(1));
  const goToAddress = () => advance(legalInfoSpecs, () => goBizSub(2));

  const mkAddress = (a: any) => ({
    line1: a.line1,
    ...(a.line2 ? { line2: a.line2 } : {}),
    city: a.city,
    state: a.state,
    postalCode: a.postalCode,
    ...(a.aptNumber ? { aptNumber: a.aptNumber } : {}),
    countryCode: 'US',
  });

  // Platform signup: no business sub-steps; submit with minimal partner + owner info.
  const createPartnerPlatform = async () => {
    const v = getValues();
    if (!adminToken) {
      setAdminErr('An admin token is required for signup (it is admin-gated).');
      return;
    }
    setSubmitting(true);
    setAdminErr('');
    try {
      const email = resumeEmail ?? v.email;
      const res = await portalRegisterPartner({ env: app.env }, app.portalToken!, adminToken, {
        partner: { companyName: `${v.ownerFirst} ${v.ownerLast}`, email, ...(v.pw ? { password: v.pw } : {}) },
      });
      setIssuedKey(res.apiKey);
      app.signIn({
        partnerName: res.partnerName,
        companyName: res.companyName,
        email,
        partner: 'trusted',
        apiKey: res.apiKey,
      });
      goStep('key');
    } catch (ex: any) {
      setAdminErr(ex?.status === 401 ? 'Admin token rejected (401).' : ex?.message || 'Signup failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // Business signup: full KYB data from the three sub-steps.
  const createPartner = async () => {
    stepAttemptedRef.current = true;
    const extraSpecs = operatingSameAsLegal ? legalAddressSpecs : [...legalAddressSpecs, ...addressSpecs];
    const err = checkRequired(extraSpecs);
    if (err) {
      setSubmitErr(err);
      return;
    }
    if (!adminToken) {
      setAdminErr('An admin token is required for signup (it is admin-gated).');
      return;
    }
    setSubmitting(true);
    setSubmitErr('');
    setAdminErr('');
    try {
      const v = getValues();
      const email = resumeEmail ?? v.email;
      const legalAddr = mkAddress(v.legalAddress);
      const operatingAddr = v.operatingSameAsLegal ? legalAddr : mkAddress(v.address);
      const res = await portalRegisterPartner({ env: app.env }, app.portalToken!, adminToken, {
        partner: { companyName: v.legalName, email, ...(v.pw ? { password: v.pw } : {}) },
        business: {
          name: v.legalName,
          legalName: v.legalName,
          phoneNumber: v.phone,
          businessType: v.businessType,
          taxInfo: { type: v.taxIdType, identifier: v.taxId },
          industry: { naicsCode: v.industry?.naicsCode, ...(v.industry?.name ? { name: v.industry.name } : {}) },
          contactFirstName: v.ownerFirst,
          contactLastName: v.ownerLast,
          ...(v.website ? { website: v.website } : {}),
          ...(v.description ? { description: v.description } : {}),
          address: operatingAddr,
          legalAddress: legalAddr,
          owner: {
            firstName: v.ownerFirst,
            lastName: v.ownerLast,
            email: email,
            ...(v.dob instanceof Date ? { dateOfBirth: v.dob.toISOString().split('T')[0] } : {}),
            ...(v.ownerPhone ? { phoneNumber: v.ownerPhone } : {}),
          },
        },
      });
      setIssuedKey(res.apiKey);
      app.signIn({
        partnerName: res.partnerName,
        companyName: res.companyName,
        email,
        partner: 'self-serve',
        apiKey: res.apiKey,
      });
      goStep('key');
    } catch (ex: any) {
      setAdminErr(ex?.status === 401 ? 'Admin token rejected (401).' : ex?.message || 'Signup failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // Left-panel milestones vary by account type.
  const milestones = isPlatform
    ? [
        { label: 'Create account', sub: 'Your partner login', steps: ['account'] },
        { label: 'About you', sub: 'Account type & identity', steps: ['type', 'you'] },
        { label: 'API key', sub: 'Copy it once, store it safe', steps: ['key'] },
      ]
    : [
        { label: 'Create account', sub: 'Your partner login', steps: ['account'] },
        { label: 'About you', sub: 'Account type & identity', steps: ['type', 'you'] },
        { label: 'Your business', sub: 'The entity you pay from', steps: ['business'] },
        { label: 'API key', sub: 'Copy it once, store it safe', steps: ['key'] },
      ];
  const activeMilestone = step === 'done' ? milestones.length : milestones.findIndex((m) => m.steps.includes(step));

  // Enter key: advance the current step without clicking Continue.
  const makeKeyDown = (advanceFn: () => void) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const target = e.target as HTMLElement;
    const tag = target.tagName;
    if (tag === 'BUTTON' || tag === 'A' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (target.closest('[role="listbox"]') || target.closest('[role="option"]') || target.closest('[role="combobox"]')) return;
    e.preventDefault();
    advanceFn();
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div
        style={{
          height: 64,
          borderBottom: '1px solid var(--border-static)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 28px',
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => go('/')}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <MelioLogo height={22} />
          <span style={{ width: 1, height: 18, background: 'var(--border-static)' }} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>API</span>
        </button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Already have an account?</span>
        <NakedButton color="var(--accent-text)" style={{ marginLeft: 12 }} onClick={() => go('/signin')}>
          Sign in
        </NakedButton>
      </div>

      <div className="grid-2" style={{ flex: 1, gridTemplateColumns: '0.85fr 1.15fr', gap: 0, alignItems: 'stretch' }}>
        {/* Left brand + milestone panel */}
        <div
          style={{
            background: 'linear-gradient(155deg, #1c1428, #16171f 70%)',
            padding: '56px 48px',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: -80,
              bottom: -80,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(120,73,255,.35), transparent 70%)',
            }}
          />
          <div style={{ position: 'relative', maxWidth: 360 }}>
            <h1 style={{ fontSize: 30, lineHeight: '38px', letterSpacing: '-0.02em', margin: '0 0 14px' }}>
              Start moving money in minutes
            </h1>
            <p style={{ fontSize: 15, lineHeight: '23px', color: '#9aa3b2', margin: '0 0 40px' }}>
              First create your partner account, tell us a little about you and your business, then grab a working key. You'll land in test
              mode.
            </p>
            <div
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 14,
                padding: 24,
              }}
            >
              {milestones.map((s, i) => {
                const done = activeMilestone > i,
                  active = activeMilestone === i;
                return (
                  <div key={i} style={{ display: 'flex', gap: 14, opacity: done || active ? 1 : 0.5 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: done ? '#8fd6a8' : active ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
                          border: active ? '2px solid var(--accent)' : 'none',
                        }}
                      >
                        {done ? (
                          <Icon name="checked" size={14} color="#16171f" />
                        ) : (
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{i + 1}</span>
                        )}
                      </div>
                      {i < milestones.length - 1 && (
                        <div
                          style={{
                            width: 2,
                            flex: 1,
                            minHeight: 22,
                            background: done ? '#8fd6a8' : 'rgba(255,255,255,0.12)',
                            margin: '3px 0',
                          }}
                        />
                      )}
                    </div>
                    <div style={{ paddingBottom: 18 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{s.label}</div>
                      <div style={{ fontSize: 12, color: '#9aa3b2', marginTop: 1 }}>{s.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 28, fontSize: 13, color: '#9aa3b2' }}>
              <Icon name="lock" size={15} color="#9aa3b2" /> Bank-grade encryption · SOC 2 Type II
            </div>
          </div>
        </div>

        {/* Right form panel */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '56px 48px', overflowY: 'auto' }}>
          <div style={{ width: '100%', maxWidth: 460 }}>
            {/* ── Step: account ── */}
            {step === 'account' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(goToType)}>
                <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Create your account</h2>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                  This is your partner account — your login and the owner of every business and API key. No sales call.
                </p>
                <Form {...formProps} columns={2}>
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('email')}
                    labelProps={{ label: 'Work email' }}
                    render={(props: any) => <TextField {...props} type="email" placeholder="you@company.com" />}
                  />
                  <Form.SecuredTextField
                    {...registerField('pw')}
                    colSpan={2}
                    isRequired
                    labelProps={{ label: 'Create a password' }}
                    helperTextProps={{ label: 'At least 12 characters.' }}
                    placeholder="••••••••••••"
                  />
                  <Form.SecuredTextField
                    {...registerField('pw2')}
                    colSpan={2}
                    isRequired
                    labelProps={{ label: 'Confirm password' }}
                    placeholder="••••••••••••"
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 16 }}>
                    {submitErr}
                  </Banner>
                )}
                <Button
                  fullWidth
                  size="large"
                  style={{ marginTop: 26 }}
                  rightIcon={submitting ? undefined : 'arrow-right'}
                  disabled={submitting}
                  onClick={goToType}
                >
                  {submitting ? <Spinner size={16} color="#fff" /> : 'Create account'}
                </Button>
                <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 16 }}>
                  By continuing you agree to the Melio Developer Terms.
                </p>
              </div>
            )}

            {/* ── Step: type ── */}
            {step === 'type' && (
              <div style={{ animation: 'om-fade-up .3s ease' }}>
                <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>What kind of account is this?</h2>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 24px' }}>
                  This shapes your dashboard and what the API returns. You can add businesses later either way.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <ChoiceCard
                    icon="business"
                    title="Business"
                    desc="One business, making payments for itself. We'll set up your single entity next."
                    selected={accountType === 'business'}
                    onClick={() => setAccountType('business')}
                  />
                  <ChoiceCard
                    icon="buildings"
                    title="Platform"
                    desc="A platform paying on behalf of several businesses. We'll create your key and you'll add client businesses from the dashboard."
                    selected={accountType === 'platform'}
                    onClick={() => setAccountType('platform')}
                  />
                </div>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 16 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('account')}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon="arrow-right" disabled={!accountType} onClick={goToYou}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step: you ── */}
            {step === 'you' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(isPlatform ? () => {} : goToBusinessOrKey)}>
                <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Tell us about you</h2>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                  These details help us verify your identity and keep your account secure.
                </p>
                <Form {...formProps} columns={2}>
                  <FormField
                    isRequired
                    {...registerField('ownerFirst')}
                    labelProps={{ label: 'First name' }}
                    render={(props: any) => <TextField {...props} placeholder="Dana" />}
                  />
                  <FormField
                    isRequired
                    {...registerField('ownerLast')}
                    labelProps={{ label: 'Last name' }}
                    render={(props: any) => <TextField {...props} placeholder="Reyes" />}
                  />
                  <Form.DateField
                    {...registerField('dob')}
                    colSpan={2}
                    isRequired
                    labelProps={{ label: 'Date of birth' }}
                    isTypable
                    format="MM/dd/yyyy"
                    placeholder="MM/DD/YYYY"
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('ownerPhone')}
                    labelProps={{ label: 'Personal phone number' }}
                    helperText="For account security — a mobile number works best."
                    render={(props: any) => <PhoneField {...props} placeholder="(###) ###-####" />}
                  />

                  {/* Admin token for platform flow (business flow shows it in the last sub-step) */}
                  {isPlatform && (
                    <FormField
                      colSpan={2}
                      isRequired
                      id="adminTokenYou"
                      labelProps={{ label: 'Admin token' }}
                      helperText={`Signup is admin-gated (x-admin-api-key). Against ${app.envLabel}. Prefilled on Local.`}
                      error={adminErr ? { message: adminErr } : undefined}
                      render={() => (
                        <TextField
                          id="adminTokenYou"
                          value={adminToken}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdminToken(e.target.value)}
                          isInvalid={!!adminErr}
                          placeholder="payouts admin token"
                        />
                      )}
                    />
                  )}
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 16 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('type')}>
                    Back
                  </Button>
                  {isPlatform ? (
                    <Button
                      fullWidth
                      size="large"
                      rightIcon={submitting ? undefined : 'arrow-right'}
                      disabled={submitting}
                      onClick={goToBusinessOrKey}
                    >
                      {submitting ? <Spinner size={16} color="#fff" /> : 'Create platform & reveal key'}
                    </Button>
                  ) : (
                    <Button fullWidth size="large" rightIcon="arrow-right" onClick={goToBusinessOrKey}>
                      Continue
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* ── Step: business (three sub-steps) ── */}
            {step === 'business' && (
              <div
                style={{ animation: 'om-fade-up .3s ease' }}
                onKeyDown={makeKeyDown(bizSub === 0 ? goToLegal : bizSub === 1 ? goToAddress : createPartner)}
              >
                <SubStepper steps={['Business info', 'Legal info', 'Address']} current={bizSub} />

                {/* Sub 0: Business info */}
                {bizSub === 0 && (
                  <>
                    <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Tell us about your business</h2>
                    <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                      Provide the name of your business and the industry it operates in.
                    </p>
                    <Form {...formProps} columns={2}>
                      <FormField
                        colSpan={2}
                        isRequired
                        {...registerField('legalName')}
                        labelProps={{ label: 'Business name' }}
                        helperText="How your business appears in Melio"
                        render={(props: any) => <TextField {...props} placeholder="Sunrise Bakery LLC" />}
                      />
                      <IndustryField form={form} env={app.env} name="industry" label="Business industry" isRequired colSpan={2} />
                      <FormField
                        colSpan={2}
                        isRequired
                        {...registerField('phone')}
                        labelProps={{ label: 'Business phone number' }}
                        render={(props: any) => <PhoneField {...props} placeholder="(###) ###-####" />}
                      />
                      <FormField
                        colSpan={2}
                        {...registerField('website')}
                        labelProps={{ label: 'Business website' }}
                        helperText="Business website or social media account if you have one."
                        render={(props: any) => <TextField {...props} placeholder="https://sunrisebakery.co" />}
                      />
                    </Form>
                    {submitErr && (
                      <Banner kind="critical" style={{ marginTop: 16 }}>
                        {submitErr}
                      </Banner>
                    )}
                    <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                      <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('you')}>
                        Back
                      </Button>
                      <Button fullWidth size="large" rightIcon="arrow-right" onClick={goToLegal}>
                        Continue
                      </Button>
                    </div>
                  </>
                )}

                {/* Sub 1: Legal info + legal registration address */}
                {bizSub === 1 && (
                  <>
                    <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Legal information</h2>
                    <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                      The legal entity money is paid from, and its registered address.
                    </p>
                    <Form {...formProps} columns={2}>
                      <FormField
                        colSpan={2}
                        isRequired
                        {...registerField('businessType')}
                        labelProps={{ label: 'Business type' }}
                        render={(props: any) => (
                          <SelectNew
                            {...props}
                            options={businessTypeOptions}
                            optionRenderer={(option: any) => (
                              <div>
                                <Text textStyle="body3Semi">{option.label}</Text>
                                {option.description && (
                                  <Text textStyle="body4" color="global.neutral.900">
                                    {option.description}
                                  </Text>
                                )}
                              </div>
                            )}
                            placeholder="Select a type"
                          />
                        )}
                      />
                      <FormField
                        isRequired
                        {...registerField('taxIdType')}
                        labelProps={{ label: 'Tax ID type' }}
                        render={(props: any) => <SelectNew {...props} options={taxTypeOptions} placeholder="Select" />}
                      />
                      <FormField
                        isRequired
                        {...registerField('taxId')}
                        labelProps={{ label: taxIdType === 'ein' ? 'EIN' : String(taxIdType || '').toUpperCase() }}
                        render={(props: any) => <TextField {...props} placeholder={taxIdType === 'ein' ? '84-3920481' : '123-45-6789'} />}
                      />
                      <FormField
                        colSpan={2}
                        {...registerField('description')}
                        labelProps={{ label: 'Business description' }}
                        render={(props: any) => <TextField {...props} placeholder="What your business does (optional)" />}
                      />
                    </Form>
                    {submitErr && (
                      <Banner kind="critical" style={{ marginTop: 18 }}>
                        {submitErr}
                      </Banner>
                    )}
                    <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                      <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goBizSub(0)}>
                        Back
                      </Button>
                      <Button fullWidth size="large" rightIcon="arrow-right" onClick={goToAddress}>
                        Continue
                      </Button>
                    </div>
                  </>
                )}

                {/* Sub 2: Legal address + operating address */}
                {bizSub === 2 && (
                  <>
                    <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Business addresses</h2>
                    <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                      Your legal registration address and where the business operates day to day.
                    </p>
                    <Form {...formProps} columns={2}>
                      <SectionTitle>Legal registration address</SectionTitle>
                      <AddressFields form={form} env={app.env} parentFieldName="legalAddress" label="Legal address" />
                      <SectionTitle>Operating address</SectionTitle>
                      <FormField
                        colSpan={2}
                        labelProps={{ label: 'Operating address', isHidden: true }}
                        render={() => (
                          <Checkbox
                            value={operatingSameAsLegal}
                            onChange={(checked: boolean) => setValue('operatingSameAsLegal', checked)}
                            label="Same as legal registration address"
                          />
                        )}
                      />
                      {!operatingSameAsLegal && (
                        <AddressFields form={form} env={app.env} parentFieldName="address" label="Operating address" />
                      )}
                      <FormField
                        colSpan={2}
                        isRequired
                        id="adminToken"
                        labelProps={{ label: 'Admin token' }}
                        helperText={`Signup is admin-gated (x-admin-api-key). Sent only on the create call, against ${app.envLabel}. Prefilled on Local.`}
                        error={adminErr ? { message: adminErr } : undefined}
                        render={() => (
                          <TextField
                            id="adminToken"
                            value={adminToken}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdminToken(e.target.value)}
                            isInvalid={!!adminErr}
                            placeholder="payouts admin token"
                          />
                        )}
                      />
                    </Form>
                    {submitErr && (
                      <Banner kind="critical" style={{ marginTop: 18 }}>
                        {submitErr}
                      </Banner>
                    )}
                    <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                      <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goBizSub(1)}>
                        Back
                      </Button>
                      <Button
                        fullWidth
                        size="large"
                        rightIcon={submitting ? undefined : 'arrow-right'}
                        disabled={submitting}
                        onClick={createPartner}
                      >
                        {submitting ? <Spinner size={16} color="#fff" /> : 'Create business & reveal key'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Step: key ── */}
            {step === 'key' && <KeyReveal apiKey={issuedKey} onContinue={() => goStep('done')} />}

            {/* ── Step: done ── */}
            {step === 'done' && (
              <div style={{ animation: 'om-fade-up .3s ease', textAlign: 'center' }}>
                <Illustration name="celebration" size={130} style={{ margin: '0 auto 8px' }} />
                <h2 style={{ margin: 0, fontSize: 26, letterSpacing: '-0.01em' }}>You're in test mode</h2>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px auto 24px', maxWidth: 380 }}>
                  {isPlatform
                    ? 'Your partner account and a sandbox key are ready. Add client businesses from the dashboard to start paying.'
                    : "Your account, first business, and a sandbox key are ready. Here's what's left before your first live payment."}
                </p>
                <div style={{ textAlign: 'left', border: '1px solid var(--border-static)', borderRadius: 12, padding: 20 }}>
                  <Tracker
                    vertical
                    current={isPlatform ? 1 : 2}
                    steps={
                      isPlatform
                        ? [
                            { label: 'Partner account created', sub: watch('email') || 'you@company.com' },
                            { label: 'Add your first client business', sub: 'Create one from the dashboard' },
                            { label: 'Add funding sources & delivery methods', sub: 'Where money comes from and goes' },
                            { label: 'Create your first payment', sub: 'Watch it move through the tracker' },
                          ]
                        : [
                            { label: 'Partner account created', sub: watch('email') || 'you@company.com' },
                            { label: 'First business added', sub: watch('legalName') || 'Sunrise Bakery LLC' },
                            { label: 'Add a funding source & delivery method', sub: 'Where money comes from and goes' },
                            { label: 'Create your first payment', sub: 'Watch it move through the tracker' },
                          ]
                    }
                  />
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                  <Button fullWidth size="large" onClick={() => go('/dashboard')} rightIcon="arrow-right">
                    Open dashboard
                  </Button>
                  <Button variant="tertiary" size="large" onClick={() => go('/reference')}>
                    View quickstart
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { Onboarding, KeyReveal };
